import moment from "moment";
import { FunctionComponent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { fetchPut, fetchGet } from "../../../service/api_client";
import { currencyFormat } from "../../../utils/currency_format";
import { currencies } from "../../accounts/models/party_model";
import { po_approval_status, POType } from "../model/purchase_order_model";
import { mdiHistory, mdiInformationOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { POLineItemCard } from "../purchase_order_create/components/po_line_item_card";
import "../../../styles/responsive_shrink.css";
import { usePurchaseOrder } from "../contexts/po-creation-context";
import { getExchangeRate } from "../purchase_order_create/service/purchase_order_repository";
import ApprovalBottomSheetWidgetV2 from "./components/bottom_sheetv2";
import DrawerModal, {
  DrawerModalLoaction,
} from "../../../ui/new_components/common/drawer_modal";
import ActionHistory from "../purchase_order_create/components/transfer_logs";
import ApprovalConfirmModal from "./components/approval_confirm_modal";

interface PurchaseOrderApprovalScreenProps {}

const ReadField: FunctionComponent<{
  label: string;
  value?: any;
  className?: string;
}> = ({ label, value, className }) => (
  <div className={`flex flex-col ${className ?? ""}`}>
    <span className="text-gray-400 text-xs">{label}</span>
    <span className="font-semibold break-words text-gray-600">
      {value === undefined || value === null || value === "" ? "--" : value}
    </span>
  </div>
);

const PurchaseOrderApprovalScreen: FunctionComponent<
  PurchaseOrderApprovalScreenProps
> = (props) => {
  const { id } = useParams<{ id: string }>();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/purchase-order`;
  const { showToast } = useToast();
  const [ShowHistory, setShowHistory] = useState<any>();
  const [ShowHistory1, setShowHistory1] = useState<any>();
  const { purchaseOrder, setPurchaseOrder } = usePurchaseOrder();
  const [confirmAction, setConfirmAction] = useState<{
    approval_status: string;
    level: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = ({
    approval_status,
    level,
  }: {
    approval_status: string;
    level: string;
  }) => {
    setConfirmAction({ approval_status, level });
  };

  const handleConfirm = async (reason: string) => {
    if (!confirmAction) return;
    setSubmitting(true);
    try {
      const res = await fetchPut(url + "/" + purchaseOrder.id + "/approval", {
        approval_status: confirmAction.approval_status,
        level: confirmAction.level,
        id: purchaseOrder.id,
        reason,
        purchaseOrder,
      });

      if (res.success) {
        setPurchaseOrder((o) => ({ ...o, ...res.data }));
        showToast({ type: ToastType.success, text: res.message });
        setConfirmAction(null);
      } else showToast({ type: ToastType.error, text: res.error });
    } finally {
      setSubmitting(false);
    }
  };

  const getCurrencyExchangeRate = async () => {
    try {
      const formattedDate = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"

      const exchangeRate = await getExchangeRate({
        from: "USD",
        to: "INR",
        date: formattedDate,
      });

      setPurchaseOrder((o) => ({
        ...o,
        vendor_currency_to_inr: exchangeRate ?? 0,
      }));
    } catch (error) {
      console.error("Error getting exchange rate:", error);
    } finally {
    }
  };

  useEffect(() => {
    if (purchaseOrder.vendor_currency) {
      getCurrencyExchangeRate();
    }
  }, [purchaseOrder.vendor_currency]);

  useEffect(() => {
    if (id) getData(id);
  }, []);

  const getData = async (id: string) => {
    const res = await fetchGet(url + "/" + id);
    if (res.success) {
      if (!res.data.vendor_currency) {
        res.data.vendor_currency = currencies.rupee;
        res.data.vendor_currency_to_inr = 1;
      }
      if (res.data.items === undefined || res.data.items === null) {
        res.data.items = [];
      }
      setPurchaseOrder(res.data);
    } else showToast({ type: ToastType.error, text: res.error });
  };

  return (
    <>
      {ShowHistory && (
        <DrawerModal
          id="po_approval_history_drawer"
          location={DrawerModalLoaction.right}
          show={ShowHistory !== undefined}
          onClose={() => setShowHistory(undefined)}
          title="History"
        >
          <ActionHistory po_no={ShowHistory} data={ShowHistory1} />
        </DrawerModal>
      )}
      {confirmAction && (
        <ApprovalConfirmModal
          show={confirmAction !== null}
          approval_status={confirmAction.approval_status}
          level={confirmAction.level}
          isSubmitting={submitting}
          onConfirm={handleConfirm}
          onCancel={() => {
            if (!submitting) setConfirmAction(null);
          }}
        />
      )}
      <div className="mobile-shrink relative bg-myBgcolor h-screen px-2 md:px-5 space-y-4 overflow-auto">
        <div className="flex flex-wrap items-center justify-between py-2 sticky top-0 bg-myBgcolor z-20 gap-2">
          <div className="text-xl font-bold border-l-4 border-myPrimaryColor pl-2">
            Purchase Order{" "}
            <span className="text-sm">#{purchaseOrder.po_no}</span>
          </div>
          <div className="flex items-center gap-3 my-1">
            <div className="flex gap-2 items-center">
              <span className="text-sm font-semibold text-gray-500">Date</span>
              <span className="text-sm font-semibold text-gray-700">
                {purchaseOrder.date
                  ? moment(purchaseOrder.date).format("DD-MM-YYYY")
                  : "--"}
              </span>
            </div>
            {purchaseOrder.id && (
              <button
                id="po_approval_history_btn"
                title="Logs"
                onClick={() => {
                  setShowHistory(purchaseOrder.po_no);
                }}
                className="rounded-full p-2 hover:scale-105 transform duration-150 bg-myPrimaryColor text-white shadow cursor-pointer flex items-center justify-center"
              >
                <Icon path={mdiHistory} className="h-6 w-6  "></Icon>
              </button>
            )}
          </div>
        </div>

        {purchaseOrder.approval_status === po_approval_status.rejected &&
          purchaseOrder.commets.length > 0 && (
            <p>
              <b>Reason of rejection :</b>
              {purchaseOrder.commets[purchaseOrder.commets.length - 1].comment}
            </p>
          )}

        <div className="bg-white rounded-lg p-3">
          <span className="text-gray-400 text-xs">PO Type</span>
          <div className="font-bold">{purchaseOrder.type ?? "--"}</div>
          {purchaseOrder.type === POType["Job Work PO"] && (
            <p className="text-xs font-semibold tracking-widest text-blue-700 mb-2">
              Job Work PO mode active: Split totals for Inventory and Payable
              Services. Only Service lines will attract GST.
            </p>
          )}
        </div>
        <div className="bg-white rounded-lg p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div className="sm:col-span-2">
              <ReadField label="Vendor" value={purchaseOrder.party?.name} />
            </div>
            <ReadField
              label="Vendor Registered Office"
              value={purchaseOrder.party.mail_detail.address.line1}
            />
            <ReadField
              label="Line 2"
              value={purchaseOrder.party.mail_detail.address.line2}
            />
            <ReadField
              label="Country"
              value={purchaseOrder.party.mail_detail.address.country}
            />
            <ReadField
              label="State"
              value={purchaseOrder.party.mail_detail.address.state}
            />
            <ReadField
              label="City"
              value={purchaseOrder.party.mail_detail.address.city}
            />
            <ReadField
              label="Pincode"
              value={purchaseOrder.party.mail_detail.address.pincode}
            />
            <ReadField
              label="GST Reg"
              value={purchaseOrder.party.tax_detail.reg_type}
            />
            <ReadField
              label="GST No"
              value={purchaseOrder.party.tax_detail.gst_no[0]}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-3">
            <h1 className="font-semibold mb-2">Bill To</h1>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <ReadField
                label="Name"
                value={purchaseOrder.bill_to?.name}
                className="col-span-2"
              />
              <ReadField
                label="GST No"
                value={purchaseOrder.bill_to?.gst_number}
                className="col-span-2"
              />
              <ReadField
                label="Address"
                value={purchaseOrder.bill_to?.address.line1}
              />
              <ReadField
                label="Line 2"
                value={purchaseOrder.bill_to?.address.line2}
              />
              <ReadField
                label="Country"
                value={purchaseOrder.bill_to?.address.country}
              />
              <ReadField
                label="State"
                value={purchaseOrder.bill_to?.address.state}
              />
              <ReadField
                label="City"
                value={purchaseOrder.bill_to?.address.city}
              />
              <ReadField
                label="Pincode"
                value={purchaseOrder.bill_to?.address.pincode}
              />
            </div>
          </div>
          <div className="bg-white rounded-lg p-3">
            <h1 className="font-semibold mb-2">Shipping Profile</h1>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <ReadField
                label="Shipping profile"
                value={purchaseOrder.warehouse?.name}
                className="col-span-2"
              />
              <ReadField
                label="Address"
                value={purchaseOrder.shipping_profile.address.line1}
              />
              <ReadField
                label="Line 2"
                value={purchaseOrder.shipping_profile.address.line2}
              />
              <ReadField
                label="Country"
                value={purchaseOrder.shipping_profile.address.country}
              />
              <ReadField
                label="State"
                value={purchaseOrder.shipping_profile.address.state}
              />
              <ReadField
                label="City"
                value={purchaseOrder.shipping_profile.address.city}
              />
              <ReadField
                label="Pincode"
                value={purchaseOrder.shipping_profile.address.pincode}
              />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-3 w-full sm:w-1/2 md:w-1/3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <ReadField
              label="Currency"
              value={
                purchaseOrder.vendor_currency
                  ? `${purchaseOrder.vendor_currency.formal_name} (${purchaseOrder.vendor_currency.symbol})`
                  : undefined
              }
            />
            <ReadField
              label={"Exchange rate (₹)"}
              value={purchaseOrder.vendor_currency_to_inr}
            />
          </div>
          <div className="flex gap-1 text-gray-500 text-xs mt-2 items-center">
            <Icon path={mdiInformationOutline} size={0.8}></Icon>
            INR Per {purchaseOrder.vendor_currency.short_name} Eg: INR 70 = USD
            1 then enter 70
          </div>
        </div>
        <div className="my-5">
          <h3 className="text-sm font-bold">Items</h3>
          <div className="mt-2 flex flex-col gap-3">
            {purchaseOrder.items.map((item, index) => (
              <POLineItemCard
                key={item.id}
                variant={
                  purchaseOrder?.type === POType["Job Work PO"]
                    ? "jobwork"
                    : "regular"
                }
                mode="approve"
                currency={purchaseOrder.vendor_currency}
                data={item}
                onSubmit={() => {}}
              />
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 md:divide-x-2  text-sm  bg-white rounded-lg p-3  ">
          <div className=" flex flex-col gap-2 md:pr-5 ">
            <ReadField
              label="Delivery Date"
              value={
                purchaseOrder.delivery_date
                  ? moment(purchaseOrder.delivery_date).format("DD-MM-YYYY")
                  : undefined
              }
            />
            <ReadField label="Delivery terms" value={purchaseOrder.ship_term} />
            <ReadField
              label="Payment terms"
              value={purchaseOrder.payment_term}
            />
          </div>

          <div className=" flex flex-col gap-1 md:pl-5 ">
            <div className="flex justify-between">
              <div className="">Subtotal</div>
              <div className="">
                {currencyFormat(
                  purchaseOrder.subtotal,
                  purchaseOrder.vendor_currency.short_name,
                )}
              </div>
            </div>{" "}
            <div className="flex justify-between ">
              <div className="flex  gap-2 items-end ">
                <label htmlFor="tax_desc">CGST </label>{" "}
              </div>

              <div className="">
                +{" "}
                {currencyFormat(
                  purchaseOrder.cgst,
                  purchaseOrder.vendor_currency.short_name,
                )}
              </div>
            </div>
            <div className="flex justify-between ">
              <div className="flex  gap-2 items-end ">
                <label htmlFor="tax_desc">SGST </label>{" "}
              </div>

              <div className="">
                +{" "}
                {currencyFormat(
                  purchaseOrder.sgst,
                  purchaseOrder.vendor_currency.short_name,
                )}
              </div>
            </div>
            <div className="flex justify-between ">
              <div className="flex  gap-2 items-end ">
                <label htmlFor="tax_desc">IGST</label>{" "}
              </div>

              <div className="">
                +{" "}
                {currencyFormat(
                  purchaseOrder.igst,
                  purchaseOrder.vendor_currency.short_name,
                )}
              </div>
            </div>
            <div className="flex justify-between">
              <div className="">Paid</div>
              <div className="">
                {currencyFormat(
                  purchaseOrder.paid,
                  purchaseOrder.vendor_currency.short_name,
                )}
              </div>
            </div>
            <hr />
            <div className="flex justify-between font-bold ">
              <p>Total</p>{" "}
              <div className="">
                {currencyFormat(
                  purchaseOrder.subtotal +
                    (purchaseOrder?.cgst +
                      purchaseOrder?.sgst +
                      purchaseOrder?.igst) -
                    purchaseOrder.paid,
                  purchaseOrder.vendor_currency.short_name,
                )}
              </div>
            </div>
            <hr />
            {purchaseOrder.vendor_currency.short_name !==
              currencies.rupee.short_name && (
              <div className="flex justify-between font-bold ">
                <p>Total in INR</p>{" "}
                <div className="">
                  {currencyFormat(
                    purchaseOrder.total * purchaseOrder.vendor_currency_to_inr,
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="lg:sticky lg:bottom-0 right-0 mt-3 mb-3 w-full rounded-t-md bg-white shadow-sm py-3 px-5 border">
          <h3 className="text-sm font-semibold">Approval status</h3>
          <ApprovalBottomSheetWidgetV2 data={purchaseOrder} onSubmit={submit} />
        </div>
      </div>
    </>
  );
};

export default PurchaseOrderApprovalScreen;
